import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, redirect } from "@tanstack/react-router";
import { Loader2, MonitorSmartphone } from "lucide-react";

import { type ActiveSession, securityApi } from "../lib/api";
import { useAuthStore } from "../lib/auth";
import { toast } from "../lib/toast";

export const Route = createFileRoute("/_app/settings/security")({
  beforeLoad: () => {
    if (useAuthStore.getState().user?.role !== "admin") throw redirect({ to: "/settings" });
  },
  component: SecuritySettingsPage,
});

function SecuritySettingsPage() {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: ["security:sessions"],
    queryFn: () => securityApi.listSessions(),
  });
  const revoke = useMutation({
    mutationFn: securityApi.revokeSession,
    onSuccess: () => {
      toast.success("Session revoked.");
      queryClient.invalidateQueries({ queryKey: ["security:sessions"] });
    },
    onError: (error: Error) => toast.error(error.message),
  });

  return (
    <div className="space-y-4">
      <header>
        <h2 className="flex items-center gap-2 text-xl font-semibold">
          <MonitorSmartphone className="size-5 opacity-70" aria-hidden /> Active sessions
        </h2>
        <p className="mt-1 text-sm opacity-60">
          Review signed-in devices across the company and revoke access immediately.
        </p>
      </header>
      {query.isLoading ? (
        <p className="flex items-center gap-2 text-sm opacity-60">
          <Loader2 className="size-4 animate-spin" />
          Loading sessions…
        </p>
      ) : query.error ? (
        <p role="alert" className="text-sm text-red-600">
          {query.error.message}
        </p>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-[var(--border)] bg-[var(--surface)]">
          <table className="w-full min-w-[720px] text-left text-sm">
            <thead className="border-b border-[var(--border)] text-xs uppercase opacity-60">
              <tr>
                <th className="px-4 py-3">Employee</th>
                <th className="px-4 py-3">Device</th>
                <th className="px-4 py-3">IP</th>
                <th className="px-4 py-3">Last active</th>
                <th className="px-4 py-3 text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {query.data?.items.map((session) => (
                <SessionRow
                  key={session.id}
                  session={session}
                  busy={revoke.isPending}
                  onRevoke={() => revoke.mutate(session.id)}
                />
              ))}
            </tbody>
          </table>
          {query.data?.items.length === 0 && (
            <p className="p-8 text-center text-sm opacity-60">No active sessions.</p>
          )}
        </div>
      )}
    </div>
  );
}

function SessionRow({
  session,
  busy,
  onRevoke,
}: { session: ActiveSession; busy: boolean; onRevoke: () => void }) {
  return (
    <tr className="border-b border-[var(--border)] last:border-0">
      <td className="px-4 py-4">
        <div className="font-medium">{session.user_name || "Name pending"}</div>
        <div className="text-xs opacity-60">{session.user_email}</div>
      </td>
      <td className="max-w-xs px-4 py-4">
        <div className="truncate text-xs opacity-70" title={session.user_agent ?? undefined}>
          {session.user_agent || "Unknown device"}
        </div>
        {session.is_current && (
          <span className="text-xs font-medium text-emerald-600">Current session</span>
        )}
      </td>
      <td className="px-4 py-4 font-mono text-xs opacity-70">{session.ip || "Unknown"}</td>
      <td className="px-4 py-4 text-xs opacity-70">
        {new Date(session.last_seen_at).toLocaleString()}
      </td>
      <td className="px-4 py-4 text-right">
        <button
          type="button"
          disabled={busy}
          onClick={onRevoke}
          className="min-h-11 rounded-md border border-red-400 px-3 text-sm text-red-600 disabled:opacity-40"
        >
          Revoke
        </button>
      </td>
    </tr>
  );
}
